import { useState, useCallback, useMemo } from "react";
import { solveAnagram, type ScoredWord } from "../services/wordAnalyzer";
import { useI18n } from "../i18n/I18nContext";

export default function AnagramSolver() {
  const { t } = useI18n();
  const [tiles, setTiles] = useState<string>("");
  const [minLength, setMinLength] = useState<number>(2);
  const [maxLength, setMaxLength] = useState<number>(15);
  const [showBingosOnly, setShowBingosOnly] = useState<boolean>(false);
  const [results, setResults] = useState<ScoredWord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSolve = useCallback(() => {
    const cleanedTiles = tiles.trim();

    if (!cleanedTiles) {
      return;
    }

    setLoading(true);
    setSearched(true);

    // Use setTimeout to allow UI to update
    setTimeout(() => {
      const words = solveAnagram(cleanedTiles, minLength, maxLength);
      setResults(words);
      setLoading(false);
    }, 100);
  }, [tiles, minLength, maxLength]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSolve();
    }
  };

  // Filter results based on bingo checkbox - updates instantly
  const displayResults = useMemo(() => {
    if (showBingosOnly) {
      return results.filter(w => w.length >= 7);
    }
    return results;
  }, [results, showBingosOnly]);

  const bingoWords = results.filter(w => w.length >= 7);
  const tileCount = tiles.replace(/[^A-Za-z?*]/g, '').length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-page via-surface to-page py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold font-heading text-primary mb-3">{t('anagramTitle')}</h1>
          <p className="text-lg text-secondary max-w-2xl mx-auto">
            {t('anagramSubtitle')}
          </p>
        </div>

        {/* Strategy Tip */}
        <div className="bg-surface/80 backdrop-blur-theme border border-subtle p-4 mb-8 rounded-card">
          <h2 className="font-semibold text-primary mb-2">🎯 {t('anagramProTipTitle')}</h2>
          <p className="text-secondary text-sm">
            {t('anagramProTipText')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="lg:col-span-2">
            <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6">
              <h2 className="text-xl font-semibold text-primary mb-6">{t('anagramTilesTitle')}</h2>

              {/* Tile Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary mb-2">
                  {t('anagramTilesLabel')}
                </label>
                <input
                  type="text"
                  value={tiles}
                  onChange={(e) => setTiles(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder={t('anagramPlaceholder')}
                  className="w-full border-2 border-input-border bg-input-bg rounded-lg px-4 py-3 text-2xl font-mono font-bold tracking-wider focus:outline-none focus:border-anagram transition-colors uppercase text-primary"
                  maxLength={15}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-secondary">
                    {t('anagramTilesEntered', { count: tileCount, s: tileCount !== 1 ? 's' : '' })}
                  </span>
                  <button
                    onClick={() => setTiles('')}
                    className="text-sm text-muted hover:text-secondary underline"
                  >
                    {t('anagramClear')}
                  </button>
                </div>
              </div>

              {/* Length Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary mb-3">
                  {t('anagramLengthLabel', { min: minLength, max: maxLength })}
                </label>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-muted">{t('anagramMinLabel', { value: minLength })}</label>
                    <input
                      type="range"
                      min="2"
                      max="15"
                      value={minLength}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setMinLength(val);
                        if (val > maxLength) setMaxLength(val);
                      }}
                      className="w-full h-2 bg-surface-raised rounded-lg appearance-none cursor-pointer accent-anagram"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted">{t('anagramMaxLabel', { value: maxLength })}</label>
                    <input
                      type="range"
                      min="2"
                      max="15"
                      value={maxLength}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setMaxLength(val);
                        if (val < minLength) setMinLength(val);
                      }}
                      className="w-full h-2 bg-surface-raised rounded-lg appearance-none cursor-pointer accent-anagram"
                    />
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showBingosOnly}
                    onChange={(e) => setShowBingosOnly(e.target.checked)}
                    className="mr-3 w-5 h-5 text-anagram focus:ring-anagram rounded"
                  />
                  <span className="text-secondary">
                    {t('anagramShowBingosOnly')}
                  </span>
                </label>
              </div>

              {/* Solve Button */}
              <button
                onClick={handleSolve}
                disabled={!tiles.trim() || loading}
                className="w-full bg-anagram hover:bg-anagram-hover text-white font-bold py-3 px-6 rounded-btn disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
              >
                {loading ? t('anagramSolving') : t('anagramFindWords')}
              </button>

              {/* Quick Examples */}
              <div className="mt-6">
                <h3 className="text-sm font-medium text-secondary mb-2">{t('anagramTryExamples')}</h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setTiles('RETAINS')}
                    className="px-3 py-1 text-sm bg-surface-raised hover:bg-surface-raised/80 rounded-lg transition-colors"
                  >
                    RETAINS
                  </button>
                  <button
                    onClick={() => setTiles('AERSTQZ')}
                    className="px-3 py-1 text-sm bg-surface-raised hover:bg-surface-raised/80 rounded-lg transition-colors"
                  >
                    AERSTQZ
                  </button>
                  <button
                    onClick={() => setTiles('RETA?NS')}
                    className="px-3 py-1 text-sm bg-surface-raised hover:bg-surface-raised/80 rounded-lg transition-colors"
                  >
                    {t('anagramExampleWithBlank')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-primary mb-4">{t('anagramResultsSummary')}</h3>

              {searched ? (
                <div className="space-y-4">
                  <div className="border-b border-subtle pb-3">
                    <div className="text-sm text-muted">{t('anagramTotalWordsFound')}</div>
                    <div className="text-3xl font-bold text-anagram-text">{results.length}</div>
                  </div>

                  {bingoWords.length > 0 && (
                    <div className="border-b border-subtle pb-3">
                      <div className="text-sm text-muted">🏆 {t('anagramBingoWords')}</div>
                      <div className="text-3xl font-bold text-success">{bingoWords.length}</div>
                      <div className="text-sm text-muted mt-1">{t('anagramBingoBonus')}</div>
                    </div>
                  )}

                  {results.length > 0 && (
                    <>
                      <div className="border-b border-subtle pb-3">
                        <div className="text-sm text-muted">{t('anagramHighestScore')}</div>
                        <div className="text-2xl font-bold text-primary">{results[0].score} {t('wordValidatorPointsSuffix')}</div>
                        <div className="text-sm text-muted mt-1">{results[0].word}</div>
                      </div>

                      <div>
                        <div className="text-sm text-muted">{t('anagramAverageScore')}</div>
                        <div className="text-2xl font-bold text-primary">
                          {Math.round(results.reduce((sum, w) => sum + w.score, 0) / results.length)} {t('wordValidatorPointsSuffix')}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-muted text-sm">
                  {t('anagramSummaryPrompt')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Results */}
        {searched && (
          <div className="mt-6">
            <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-primary">
                  {displayResults.length > 0
                    ? t('anagramWordsFound', { count: displayResults.length, s: displayResults.length !== 1 ? 's' : '' })
                    : t('anagramNoWordsFound')}
                </h2>
                {loading && (
                  <span className="text-sm text-muted">{t('wordFinderLoading')}</span>
                )}
              </div>

              {displayResults.length === 0 ? (
                <div className="text-center py-12 text-muted">
                  <p className="text-lg">{t('anagramNoWordsText')}</p>
                  <p className="text-sm mt-2">{t('anagramNoWordsHint')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-subtle">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-secondary">{t('anagramTableRank')}</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-secondary">{t('anagramTableWord')}</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">{t('anagramTableLength')}</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-secondary">{t('anagramTableScore')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayResults.slice(0, 50).map((word, index) => {
                        const isBingo = word.length >= 7;
                        return (
                          <tr
                            key={`${word.word}-${index}`}
                            className={`border-b border-subtle hover:bg-anagram-subtle/40 transition-colors ${
                              isBingo ? 'bg-success-subtle' : ''
                            }`}
                          >
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${
                                isBingo ? 'bg-success text-white' : 'bg-anagram-subtle text-anagram-text'
                              }`}>
                                {index + 1}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-lg text-primary">
                                  {word.word}
                                </span>
                                {isBingo && (
                                  <span className="text-sm bg-success text-white px-2 py-1 rounded font-bold">
                                    {t('anagramBingoBadge')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center text-secondary">
                              {word.length}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className={`inline-block font-bold px-3 py-1 rounded-lg ${
                                isBingo ? 'bg-success text-white' : 'bg-anagram text-white'
                              }`}>
                                {word.score}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {displayResults.length > 50 && (
                    <p className="text-center text-sm text-muted mt-4">
                      {t('anagramShowingTop', { count: displayResults.length })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
